import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';

/**
 * Manages the campaign goals section: allows adding, editing, validating and emitting goal data for a campaign.
 * @component
 * @selector app-goals
 * @example
 *   <app-goals
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)">
 *   </app-goals>
 *
 * Purpose:
 *   This component provides a UI and logic for creating and editing one or more campaign goals.
 *   It maintains an internal goals array, validates fields (including date ranges and numeric limits),
 *   enforces a 7-digit positive limit for target values, and emits normalized goal data outward.
 *
 * Lifecycle hooks:
 *   - ngOnInit: Initializes the internal goals from campaignData when available.
 *   - ngOnChanges: Updates internal goals from external campaignData only on initial load or when goal count changes
 *                 (avoids overwriting user edits/focus loss).
 *
 * Inputs:
 * @input {boolean} isExpanded - Controls whether the collapsible goals section is expanded (default: true).
 * @input {any} campaignData - External campaign object containing initial goals; used to pre-populate the form.
 * @input {boolean} isCompleted - Indicates whether the parent/editor considers this section completed.
 *
 * Outputs:
 * @output {EventEmitter<boolean>} toggle - Emitted when the basic info/collapsible section is toggled (expansion state).
 * @output {EventEmitter<void>} done - Emitted when the user marks the goals section done (validates or allows optional empty).
 * @output {EventEmitter<any>} dataChange - Emitted whenever the internal goals change; payload contains normalized goals array.
 *
 * Key methods and behaviors:
 *   - trackByIndex(index): Optimizes ngFor rendering by index.
 *   - onBasicInfoToggle(expanded): Emits toggle with current expansion state.
 *   - onBasicInfoDone(): Validates goals and emits done if section is valid or empty (section is optional).
 *   - addAnotherGoal(): Validates existing goals, then appends a new blank goal if valid.
 *   - removeGoal(index): Removes a goal, reindexes validation errors, and emits changes.
 *   - onGoalChange(index, field, value): Central handler for updating a goal field; enforces numeric rules for target_value,
 *       triggers validation and emits dataChange.
 *   - onTargetValueKeydown / onTargetValueInput: Prevent invalid input, enforce numeric-only and 7-digit maximum in the UI.
 *   - onDateChange(index, field, value): Updates start/end dates, validates, and emits changes.
 *   - validateGoal(index): Validates individual goal fields (name required and <=150 chars, positive numeric target <=7 digits,
 *       timeframe and custom-duration start/end date rules).
 *   - validateAllGoals(): Validates all goals and returns overall validity.
 *   - getFieldError / hasFieldError / getStyleClass: Helpers to query and present validation errors in the template.
 *   - formatDate(date): Formats a Date into "MMM d yyyy" for display.
 *   - onFormChange(): Normalizes internal goals (strings/numbers/ISO dates, status) and emits them via dataChange.
 *
 * Other details:
 *   - Provides eventOptions and timeframeOptions arrays for dropdowns (e.g., car-washes-booked, custom-duration).
 *   - Maintains goalErrors map and flags (isFormSubmitted, isAddGoalAttempted) to control when validation messages show.
 *   - Ensures external campaignData updates only overwrite internal state on initial load or when the count of goals changes
 *     to avoid disrupting user input.
 */
@Component({
  selector: 'app-goals',
  imports: [
    CollapsibleSectionComponent,
    FormsModule,
    CommonModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
  templateUrl: './goals.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './goals.component.scss',
})
export class GoalsComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  // Goals array to store multiple goals
  goals: any[] = [
    {
      goal_name: '',
      event: 'car-washes-booked',
      target_value: '',
      timeframe: 'campaign-duration',
      start_date: null,
      end_date: null,
      status: true,
    },
  ];

  // Validation errors tracking
  goalErrors: Record<number, Record<string, string>> = {};
  isFormSubmitted = false;
  isAddGoalAttempted = false;

  // Options for dropdowns
  eventOptions = [
    { label: 'Car Washes Booked', value: 'car-washes-booked' },
    { label: 'Offer Used', value: 'offer-used' },
    { label: 'Purchased Subscriptions', value: 'purchased-subscriptions' },
    { label: 'Clicked On Email', value: 'clicked-on-email' },
    { label: 'Clicked On Text Messaging', value: 'clicked-on-text-messaging' },
    { label: 'App Opened', value: 'app-opened' },
  ];

  timeframeOptions = [
    { label: 'Campaign Duration', value: 'campaign-duration' },
    { label: '7 Days', value: '7-days' },
    { label: '14 Days', value: '14-days' },
    { label: '30 Days', value: '30-days' },
    { label: 'Custom Duration', value: 'custom-duration' },
  ];

  /**
   * Initialize the component's goals array from existing campaignData.goals, mapping each goal and providing defaults for missing fields.
   * @example
   * gOnInit()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    // Initialize with existing data if provided
    if (this.campaignData && this.campaignData.goals && this.campaignData.goals.length > 0) {
      this.goals = this.campaignData.goals.map((goal: any) => ({
        goal_name: goal.goal_name || '',
        event: goal.event || 'car-washes-booked',
        target_value: goal.target_value?.toString() || '',
        timeframe: goal.timeframe || 'campaign-duration',
        start_date: goal.start_date ? new Date(goal.start_date) : null,
        end_date: goal.end_date ? new Date(goal.end_date) : null,
        status: goal.status !== undefined ? goal.status : true,
      }));
    }
  }

  /**
   * Update the component's internal goals array from external campaignData when goals change, avoiding updates triggered by the component itself to prevent focus loss.
   * @example
   * gOnChanges({ campaignData: { goals: [{ goal_name: 'Wash Campaign', event: 'car-washes-booked', target_value: 100, timeframe: 'campaign-duration', start_date: '2026-01-01', end_date: '2026-12-31', status: true }] } })
   * undefined
   * @param {{any}} {{changes}} - Angular SimpleChanges object containing input changes (typically includes campaignData).
   * @returns {{void}} Does not return a value.
   **/
  ngOnChanges(changes: any): void {
    // Only update if goals data actually changed from external source
    // Skip update if we're the ones who triggered the change (prevents focus loss)
    if (this.campaignData && this.campaignData.goals && this.campaignData.goals.length > 0) {
      // Only update if the number of goals changed or if this is initial load
      const lengthChanged = this.goals.length !== this.campaignData.goals.length;

      // Check if this is initial load (goals array is empty or has default empty values)
      const isInitialLoad =
        this.goals.length === 0 ||
        (this.goals.length === 1 && !this.goals[0].goal_name && !this.goals[0].target_value);

      if (lengthChanged || isInitialLoad) {
        this.goals = this.campaignData.goals.map((goal: any) => ({
          goal_name: goal.goal_name || '',
          event: goal.event || 'car-washes-booked',
          target_value: goal.target_value?.toString() || '',
          timeframe: goal.timeframe || 'campaign-duration',
          start_date: goal.start_date ? new Date(goal.start_date) : null,
          end_date: goal.end_date ? new Date(goal.end_date) : null,
          status: goal.status !== undefined ? goal.status : true,
        }));
      }
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  onBasicInfoToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  /**
   * Finalizes the goals step: emits the done event if there are no filled goals or if all filled goals validate; otherwise marks the form as submitted to reveal validation errors.
   * @example
   * nBasicInfoDone()
   * undefined
   * @returns {{void}} No value is returned; may emit 'done' or set isFormSubmitted to true to display validation errors.
   **/
  onBasicInfoDone(): void {
    // Goals section is optional - allow completion even if empty
    // Check if any goals have been filled (have a goal_name)
    const hasFilledGoals = this.goals.some(goal => goal.goal_name && goal.goal_name.trim() !== '');

    if (!hasFilledGoals) {
      // No goals filled, allow completion (optional section)
      this.done.emit();
    } else if (this.validateAllGoals()) {
      // Goals are filled and valid
      this.done.emit();
    } else {
      // Goals are filled but invalid, show errors
      this.isFormSubmitted = true;
    }
  }

  /**
   * Attempt to add a new empty goal to the goals list after validating all existing goals.
   * @example
   * ddAnotherGoal()
   * void
   * @returns {void} No return value — updates component state, may push a new goal and trigger onFormChange().
   */
  addAnotherGoal(): void {
    // Validate all existing goals and show errors
    this.isAddGoalAttempted = true;
    const isValid = this.validateAllGoals();

    // Only add if all existing goals are valid
    if (isValid) {
      this.isAddGoalAttempted = false; // Reset flag when successfully adding
      this.goals.push({
        goal_name: '',
        event: 'car-washes-booked',
        target_value: '',
        timeframe: 'campaign-duration',
        start_date: null,
        end_date: null,
        status: true,
      });
      this.onFormChange();
    }
  }

  /**
   * Remove a goal at the specified index, reindex remaining goal errors, and trigger form change.
   * @example
   * emoveGoal(1)
   * undefined
   * @param {{number}} {{index}} - Zero-based index of the goal to remove; does nothing if only one goal remains.
   * @returns {{void}} Void return; function updates internal state and calls onFormChange.
   **/
  removeGoal(index: number): void {
    if (this.goals.length > 1) {
      this.goals.splice(index, 1);
      // Clear errors for removed goal and reindex remaining errors
      delete this.goalErrors[index];
      const newErrors: Record<number, Record<string, string>> = {};
      Object.keys(this.goalErrors).forEach(key => {
        const oldIndex = parseInt(key, 10);
        if (oldIndex > index) {
          newErrors[oldIndex - 1] = this.goalErrors[oldIndex];
        } else if (oldIndex < index) {
          newErrors[oldIndex] = this.goalErrors[oldIndex];
        }
      });
      this.goalErrors = newErrors;
      this.onFormChange();
    }
  }

  /**
   * Update a goal field at the given index, with special handling for 'target_value' to strip non-digits, block negatives, and limit to 7 digits; then validate the goal and mark the form as changed.
   * @example
   * nGoalChange(0, 'target_value', '123-45abc')
   * undefined
   * @param {{number}} {{index}} - Index of the goal in the goals array.
   * @param {{string}} {{field}} - Field name on the goal object to update (e.g., 'target_value').
   * @param {{any}} {{value}} - New value to set; when field is 'target_value' non-numeric characters are removed, negatives are blocked, and the value is limited to 7 digits (resulting value is a Number or an empty string).
   * @returns {{void}} No return value.
   **/
  onGoalChange(index: number, field: string, value: any): void {
    // Special handling for target_value to enforce 7-digit limit and block negatives
    if (field === 'target_value') {
      let processedValue = value;

      // Convert to string for processing
      if (processedValue !== null && processedValue !== undefined) {
        processedValue = processedValue.toString();

        // Remove ALL non-numeric characters (including hyphens, negative signs, etc.)
        const numericValue = processedValue.replace(/[^0-9]/g, '');

        // Limit to 7 digits
        if (numericValue.length > 7) {
          processedValue = numericValue.substring(0, 7);
        } else {
          processedValue = numericValue;
        }

        // Convert back to number if not empty, otherwise keep as empty string
        value = processedValue === '' ? '' : Number(processedValue);
      }
    }

    this.goals[index][field] = value;
    this.validateGoal(index);
    // Clear add goal attempted flag when user starts editing
    if (this.isAddGoalAttempted) {
      this.isAddGoalAttempted = false;
    }
    this.onFormChange();
  }

  /**
   * Prevent non-numeric keystrokes and enforce a seven-digit limit on a target input while allowing editing and navigation keys.
   * @example
   * nTargetValueKeydown(0, new KeyboardEvent('keydown'))
   * undefined
   * @param {{number}} {{index}} - Index of the target input element being edited.
   * @param {{KeyboardEvent}} {{event}} - Keydown event used to allow/disallow keys and to enforce the digit limit.
   * @returns {{void}} No return value; may call event.preventDefault() to block invalid keystrokes.
   **/
  onTargetValueKeydown(index: number, event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and arrow keys
    if (
      [46, 8, 9, 27, 13, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }

    // Ensure that it is a number and stop the keypress if not
    if (
      (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
      (event.keyCode < 96 || event.keyCode > 105)
    ) {
      event.preventDefault();
    }

    // Check if adding this digit would exceed 7 digits
    const input = event.target as HTMLInputElement;
    const currentValue = input.value.replace(/[^0-9]/g, '');
    if (currentValue.length >= 7 && !event.ctrlKey) {
      // Allow backspace, delete, and arrow keys even if at limit
      if ([46, 8, 37, 38, 39, 40].indexOf(event.keyCode) === -1) {
        event.preventDefault();
      }
    }
  }

  /**
   * Sanitize numeric input for a goal's target value (allow digits only, max 7) and update the goal, validate, and notify form changes.
   * @example
   * nTargetValueInput(1, someInputEvent)
   * undefined
   * @param {number} index - Index of the goal in the goals array to update.
   * @param {Event} event - Input event from the HTML input element containing the new value.
   * @returns {void} No return value.
   */
  onTargetValueInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const { value } = input;

    // Remove ALL non-numeric characters (including hyphens, negative signs, etc.)
    const numericValue = value.replace(/[^0-9]/g, '');

    // Limit to 7 digits
    const limitedValue = numericValue.length > 7 ? numericValue.substring(0, 7) : numericValue;

    // Update the input value and goal data if changed
    if (input.value !== limitedValue) {
      input.value = limitedValue;
      this.goals[index].target_value = limitedValue === '' ? '' : Number(limitedValue);
      this.validateGoal(index);
      this.onFormChange();
    }
  }

  onDateChange(index: number, field: 'start_date' | 'end_date', value: Date): void {
    this.goals[index][field] = value;
    this.validateGoal(index);
    // Clear add goal attempted flag when user starts editing
    if (this.isAddGoalAttempted) {
      this.isAddGoalAttempted = false;
    }
    this.onFormChange();
  }

  /**
   * Validate a goal at the given index and record field-specific validation errors on this.goalErrors.
   * @example
   * alidateGoal(0)
   * // no return value; this.goalErrors is updated or cleared for index 0
   * @param {number} index - Index of the goal to validate.
   * @returns {void} No return value; updates this.goalErrors for the specified index.
   */
  validateGoal(index: number): void {
    const goal = this.goals[index];
    const errors: Record<string, string> = {};

    // Validate goal_name
    if (!goal.goal_name || goal.goal_name.trim() === '') {
      errors['goal_name'] = 'Goal name is required';
    } else if (goal.goal_name.length > 150) {
      errors['goal_name'] = 'Goal name must not exceed 150 characters';
    }

    // Validate event (always has default, but check anyway)
    if (!goal.event) {
      errors['event'] = 'Event is required';
    }

    // Validate target_value
    if (!goal.target_value || goal.target_value.toString().trim() === '') {
      errors['target_value'] = 'Target value is required';
    } else if (isNaN(Number(goal.target_value)) || Number(goal.target_value) <= 0) {
      errors['target_value'] = 'Target value must be a positive number';
    } else if (Number(goal.target_value) < 0) {
      errors['target_value'] = 'Target value cannot be negative';
    } else {
      const targetValueStr = goal.target_value.toString().replace(/[^0-9]/g, '');
      if (targetValueStr.length > 7 || Number(goal.target_value) > 9999999) {
        errors['target_value'] = 'Target value cannot exceed 7 digits';
      }
    }

    // Validate timeframe
    if (!goal.timeframe) {
      errors['timeframe'] = 'Timeframe is required';
    }

    if (goal.timeframe === 'custom-duration' && !goal.start_date)
      errors['start_date'] = 'Start date is required';
    if (goal.timeframe === 'custom-duration' && !goal.end_date)
      errors['end_date'] = 'End date is required';
    if (
      goal.timeframe === 'custom-duration' &&
      goal.start_date &&
      goal.end_date &&
      goal.start_date >= goal.end_date
    ) {
      errors['end_date'] = 'End date must be after start date';
    }

    if (Object.keys(errors).length > 0) {
      this.goalErrors[index] = errors;
    } else {
      delete this.goalErrors[index];
    }
  }

  /**
   * Validate all goals in the component by running individual goal validation and aggregating errors.
   * @example
   * validateAllGoals()
   * // returns true if all goals are valid, false if any goal has validation errors
   * @returns {boolean} True if all goals are valid, otherwise false.
   **/
  validateAllGoals(): boolean {
    this.goalErrors = {};
    let isValid = true;

    this.goals.forEach((goal, index) => {
      this.validateGoal(index);
      if (this.goalErrors[index] && Object.keys(this.goalErrors[index]).length > 0) {
        isValid = false;
      }
    });

    return isValid;
  }

  getFieldError(index: number, field: string): string {
    if (this.goalErrors[index] && this.goalErrors[index][field]) {
      return this.goalErrors[index][field];
    }
    return '';
  }

  hasFieldError(index: number, field: string): boolean {
    return Boolean(
      this.goalErrors[index] &&
      this.goalErrors[index][field] &&
      (this.isFormSubmitted || this.isAddGoalAttempted)
    );
  }

  getStyleClass(baseClass: string, index: number, field: string): string {
    return this.hasFieldError(index, field) ? `${baseClass} error` : baseClass;
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, 'MMM d yyyy') || '';
  }

  /**
   * Normalize the component's goals array (parse numeric values and format dates) and emit the processed data via the dataChange event.
   * @example
   * nFormChange()
   * // emits: { goals: [{ goal_name: 'Increase Sales', event: 'purchase', target_value: 100, timeframe: 'monthly', start_date: '2026-01-01', end_date: '2026-12-31', status: 'active' }] }
   * @returns {void} Emits a dataChange event containing the processed goals payload.
   */
  onFormChange(): void {
    const processedGoals = this.goals.map(goal => ({
      goal_name: goal.goal_name,
      event: goal.event,
      target_value: parseInt(String(goal.target_value), 10) || 0,
      timeframe: goal.timeframe,
      start_date: goal.start_date ? goal.start_date.toISOString().split('T')[0] : null,
      end_date: goal.end_date ? goal.end_date.toISOString().split('T')[0] : null,
      status: goal.status,
    }));

    const data = {
      goals: processedGoals,
    };
    this.dataChange.emit(data);
  }
}
